import { useLanguage, t } from "../../../i18n";
import React, { useMemo } from "react";
import { Container, Row, Col, Button, Card, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { HiOutlineMagnifyingGlass, HiOutlineIdentification, HiOutlineChatBubbleLeftRight, HiOutlineMapPin } from "react-icons/hi2";
import ROUTES from "../../../config/routes";
import useDocumentTitle from "../../../hooks/useDocumentTitle";
import { whyBaytBuddyCards, howBaytBuddyWorksSteps } from "../../../data/staticContent";
import WhyBaytBuddyCard from "../../../components/common/WhyBaytBuddyCard/WhyBaytBuddyCard";
import HowItWorksCard from "../../../components/common/HowItWorksCard/HowItWorksCard";
import TrustVerifiedSection from "../../../components/common/TrustVerifiedSection/TrustVerifiedSection";
import Footer from "../../../components/layout/Footer/Footer";
import "./Landing.css";
import Logo from "../../../components/common/Logo/Logo";
import AppComingSoonSection from "../../../components/common/AppComingSoonSection/AppComingSoonSection";
import RecentListingsSection from "../../../components/listing/RecentListingsSection/RecentListingsSection";
import useCityListings from "../../../features/listing/hooks/useCityListings";
import { useLocation } from "../../../features/location/context/LocationContext";
function toTitleCase(value) {
  return String(value || "").replace(/_/g, " ").replace(/\s+/g, " ").trim().toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
}
function mapGenderPreference(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "male") return "Male Only";
  if (normalized === "female") return "Female Only";
  return value || "Not specified";
}
function mapFurnished(value) {
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  const normalized = String(value || "").trim().toLowerCase();
  if (["1", "true", "yes"].includes(normalized)) return "Yes";
  if (["0", "false", "no"].includes(normalized)) return "No";
  return value ? String(value) : "No";
}
function getPrimaryImage(listing = {}) {
  if (listing?.primary_image) {
    return listing.primary_image;
  }
  if (Array.isArray(listing?.images) && listing.images.length > 0) {
    const primaryImage = listing.images.find(item => item?.is_primary) || listing.images[0];
    return primaryImage?.url || "";
  }
  return "";
}
function mapApiListingToCard(listing) {
  return {
    id: listing?.id,
    title: listing?.title || "Untitled Listing",
    image: getPrimaryImage(listing),
    type: toTitleCase(listing?.room_type),
    gender: mapGenderPreference(listing?.gender_preference),
    district: listing?.neighbourhood?.name || listing?.district || listing?.address || "Location not specified",
    city: listing?.city?.name || listing?.city_name || "",
    nationality: Array.isArray(listing?.community_preferences_data) ? listing.community_preferences_data.map(item => item?.name).filter(Boolean) : [],
    price: Number(listing?.rent || 0),
    houseType: toTitleCase(listing?.house_type),
    furnished: mapFurnished(listing?.furnished),
    address: listing?.address || "",
    createdAt: listing?.created_at || "",
    posted_ago: listing?.posted_ago || "",
    isOwner: Boolean(listing?.is_owner),
    raw: listing
  };
}
export default function Landing() {
  useLanguage();
  useDocumentTitle("Find Rooms in Saudi Arabia");
  const navigate = useNavigate();
  const {
    detectedCity,
    detectedCityId
  } = useLocation();
  const resolvedCityName = detectedCity || "Riyadh";
  const resolvedCityId = detectedCityId || "";
  const { listings, isLoading: isRecentListingsLoading } = useCityListings(resolvedCityId, resolvedCityName);
  const recentListings = useMemo(() => listings.map(mapApiListingToCard).sort((a, b) =>
    new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
  ).slice(0, 10), [listings]);
  return <div className="bb-guest-landing">
    <header className="bb-guest-landing__header">
      <Container>
        <div className="bb-guest-landing__header-inner">
          <div className="bb-guest-landing__logo" onClick={() => navigate(ROUTES.ROOT)} role="button" tabIndex={0} onKeyDown={e => {
            if (e.key === "Enter" || e.key === " ") {
              navigate(ROUTES.ROOT);
            }
          }}>

            <Logo />
          </div>

          <div className="bb-guest-landing__actions">
            <Button className="bb-explore-listing__button" variant="outline-primary" onClick={() => navigate(ROUTES.LISTINGS)}>{t("Explore Listings")}


            </Button>

            <Button className="bb-brand-btn-outline" onClick={() => navigate(ROUTES.LOGIN)}>{t("Login")}


            </Button>
          </div>
        </div>
      </Container>
    </header>

    <section className="bb-guest-hero">
      <Container>
        <Row className="align-items-center g-4">
          <Col lg={7}>
            <div className="bb-guest-hero__content">
              <span className="bb-guest-hero__badge">{t("A Saudi based roommate discovery platform")}

              </span>

              <h1 className="bb-guest-hero__title">{t("Find the right room and roommate in Saudi Arabia with more clarity and less hassle")}


              </h1>

              <p className="bb-guest-hero__subtitle">{t("BaytBuddy helps users discover room listings and shared-living options across Saudi Arabia through a simple, city-focused experience. Browse listings, create your profile, and unlock the ability to connect with listers.")}




              </p>

              <div className="bb-guest-hero__buttons">
                <Button className="bb-get-started__button" size="lg" onClick={() => navigate(ROUTES.REGISTER)}>{t("Get Started")}


                </Button>

                <Button size="lg" variant="outline-primary" onClick={() => navigate(ROUTES.LISTINGS)} className="bb-brand-btn-outline">{t("Browse Rooms")}


                </Button>
              </div>
            </div>
          </Col>

          <Col lg={5}>
            <Card className="bb-guest-hero__stats-card">
              <Card.Body>
                <div className="bb-guest-hero__stat-item">
                  <div className="bb-guest-hero__stat-icon">
                    <HiOutlineMagnifyingGlass />
                  </div>
                  <div>
                    {/* <h3>Browse Listings</h3> */}
                    <div className="bb-guest-hero__stat-title">{t("Browse Listings")}</div>
                    <p>{t("Find listings by city and budget.")}</p>
                  </div>
                </div>

                <div className="bb-guest-hero__stat-item">
                  <div className="bb-guest-hero__stat-icon">
                    <HiOutlineIdentification />
                  </div>
                  <div>
                    {/* <h3>Create Your Profile</h3> */}
                    <div className="bb-guest-hero__stat-title">{t("Create Your Profile")}</div>
                    <p>{t("Unlock full platform access.")}</p>
                  </div>
                </div>

                <div className="bb-guest-hero__stat-item">
                  <div className="bb-guest-hero__stat-icon">
                    <HiOutlineChatBubbleLeftRight />
                  </div>
                  <div>
                    {/* <h3>Connect with Listers</h3> */}
                    <div className="bb-guest-hero__stat-title">{t("Connect with Listers")}</div>
                    <p>{t("Reach out after profile completion.")}</p>
                  </div>
                </div>

                <div className="bb-guest-hero__stat-item">
                  <div className="bb-guest-hero__stat-icon">
                    <HiOutlineMapPin />
                  </div>
                  <div>
                    {/* <h3>City Based</h3> */}
                    <div className="bb-guest-hero__stat-title">{t("City Based")}</div>
                    <p>{t("Find listings based on location and preferences")}</p>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </section>

    <section className="bb-guest-recent-listings">
      <Container>
        {isRecentListingsLoading ? <div className="bb-guest-recent-listings__loader">
          <Spinner animation="border" size="sm" />
          <span>{t("Loading recent listings...")}</span>
        </div> : recentListings.length ? <RecentListingsSection listings={recentListings} city={resolvedCityName} /> : null}
      </Container>
    </section>

    <section className="bb-guest-why">
      <Container>
        <div className="bb-guest-section-header">
          <h2>{t("Why BaytBuddy?")}</h2>
          <p>{t("Built for a more practical room discovery experience in Saudi Arabia")}


          </p>
        </div>

        <Row className="g-4">
          {whyBaytBuddyCards.map(item => <Col key={item.id} xl={3} md={6}>
            <WhyBaytBuddyCard icon={item.icon} title={t(item.title)} description={t(item.description)} />

          </Col>)}
        </Row>
      </Container>
    </section>

    <section className="bb-guest-how">
      <Container>
        <div className="bb-guest-section-header">
          <h2>{t("How BaytBuddy Works?")}</h2>
          <p>{t("A simple way to discover rooms and connect more meaningfully")}</p>
        </div>

        <Row className="g-4">
          {howBaytBuddyWorksSteps.map(item => <Col key={item.id} lg={4}>
            <HowItWorksCard icon={item.icon} step={item.step} title={t(item.title)} description={t(item.description)} />

          </Col>)}
        </Row>
      </Container>
    </section>

    <section className="bb-guest-trust">
      <Container>
        <TrustVerifiedSection />
      </Container>
    </section>

    <AppComingSoonSection />

    <Footer />
  </div>;
}
